import { FormSubHeader, FormControl } from '../../../components/Form';
import { IServiceChargeSettings, PosSystemType, ServiceChargeType } from '@ready/dashboardv2api.contracts';
import { IUpdateLocationParam } from '../LocationsEditPage';
import { LOCATION_SETTINGS_ACTIONS } from '../../redux/LocationSettingsActions';
import { PosItemSelect } from './ForcedServiceCharge/PosItemSelect';
import { useParams } from 'react-router-dom';
import FormattedInput from '../../../components/FormattedInput/FormattedInput';
import React, { useState, useEffect } from 'react';
import SelectFilter, { Option } from '../../../components/SelectFilter/SelectFilter';
import TextInput from '../../../components/TextInput/TextInput';
import Toggle from '../../../components/Toggle/Toggle';
import { toNumber } from '../../../utils/numberUtils/toNumber';
import mapSelectOption from 'utils/selectListUtils/mapSelectOption';

export type ServiceChargePosIntegration = PosSystemType.bypass2 | PosSystemType.omnivore | PosSystemType.simphony2;

export interface ForcedServiceChargesProps {
  settings: IServiceChargeSettings;
  posIntegration: ServiceChargePosIntegration;
  updateLocationParam: IUpdateLocationParam;
}

const chargeTypeOptions: Option[] = [
  {
    label: 'Percent',
    value: ServiceChargeType.Percent,
  },
  {
    label: '$ Amount',
    value: ServiceChargeType.Amount,
  },
];

const serviceChargeMethodOptions: Option[] = [
  {
    label: 'Menu Item Based',
    value: 'menu-item',
  },
  {
    label: 'Amount/Percentage Based',
    value: 'amount-percentage',
  },
];

const serviceChargeAmountValue = (settings: IServiceChargeSettings): number | undefined => {
  if (!settings.serviceChargeAmount) {
    return;
  }
  if (settings.serviceChargeType === ServiceChargeType.Percent) {
    return settings.serviceChargeAmount * 100;
  } else {
    return settings.serviceChargeAmount / 100;
  }
};

const showSupportChargesFields = (posSystemType: PosSystemType): boolean => {
  return posSystemType === PosSystemType.omnivore || posSystemType === PosSystemType.simphony2 || posSystemType === PosSystemType.bypass2;
};

const ForcedServiceCharges = (props: ForcedServiceChargesProps) => {
  const { settings, updateLocationParam, posIntegration } = props;
  const { companyId, id } = useParams<{ companyId: string; id: string; tab: string }>();

  const isServiceChargeTypeAmount = settings.serviceChargeType === ServiceChargeType.Amount;
  
  // Initialize method based on current settings, defaulting to 'menu-item' for new configurations
  const [serviceChargeMethod, setServiceChargeMethod] = useState<string>(() => {
    // If there's a serviceChargeId, it's menu-item based
    // If there's a serviceChargeAmount but no serviceChargeId, it's amount-percentage based
    // If neither, default to menu-item
    if (settings.serviceChargeId) {
      return 'menu-item';
    } else if (settings.serviceChargeAmount && !settings.serviceChargeId) {
      return 'amount-percentage';
    } else {
      return 'menu-item';
    }
  });

  // Update local state when settings change from external sources
  useEffect(() => {
    if (settings.serviceChargeId) {
      setServiceChargeMethod('menu-item');
    } else if (settings.serviceChargeAmount && !settings.serviceChargeId) {
      setServiceChargeMethod('amount-percentage');
      // If we have an amount but no type, set a default type
      if (!settings.serviceChargeType) {
        updateLocationParam(ServiceChargeType.Percent, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_TYPE);
      }
    }
  }, [settings.serviceChargeId, settings.serviceChargeAmount, settings.serviceChargeType, updateLocationParam]);

  const isMenuItemBased = serviceChargeMethod === 'menu-item';

  const handleServiceChargeMethodChange = (option: Option) => {
    setServiceChargeMethod(option.value);
    
    if (option.value === 'menu-item') {
      // When switching to menu item based, clear any amount settings
      updateLocationParam(undefined, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_AMOUNT);
      updateLocationParam(undefined, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_TYPE);
    } else {
      // When switching to amount/percentage based, clear the menu item ID and set default type
      updateLocationParam(undefined, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_POS_ITEM);
      // Set a default type if none exists
      if (!settings.serviceChargeType) {
        updateLocationParam(ServiceChargeType.Percent, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_TYPE);
      }
    }
  };

  const handleChargeTypeChange = (option: Option) => {
    // For Bypass2, we handle amount clearing in the method switch, not type switch
    if (posIntegration !== PosSystemType.omnivore && posIntegration !== PosSystemType.simphony2 && posIntegration !== PosSystemType.bypass2) {
      updateLocationParam(undefined, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_AMOUNT);
    }

    updateLocationParam(option.value, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_TYPE);
  };

  const handleChargeAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const amount = event.target.value;
    let parsedAmount: number | undefined;

    if (amount) {
      if (settings.serviceChargeType === ServiceChargeType.Percent) {
        parsedAmount = toNumber(amount) / 100;
      } else if (settings.serviceChargeType === ServiceChargeType.Amount) {
        parsedAmount = toNumber(amount) * 100;
      } else {
        // Default to percent if type is null/undefined
        parsedAmount = toNumber(amount) / 100;
      }
    }

    updateLocationParam(parsedAmount, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_AMOUNT);
  };

  return (
    <>
      <div className='form-panel-separator'></div>
      <FormSubHeader label='Forced Service Charges' />
      <FormControl label='Status'>
        <Toggle
          checked={settings.serviceChargeEnabled ?? false}
          onChange={(checked) => {
            updateLocationParam(checked, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_STATUS);
          }}
        />
      </FormControl>

      {showSupportChargesFields(posIntegration) && posIntegration !== PosSystemType.bypass2 && (
        <>
          <FormControl label='Select Service Charge *'>
            <TextInput
              placeholder='Select Service Charge'
              value={settings.serviceChargeId}
              onChange={(event) => {
                updateLocationParam(event.target.value, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE);
              }}
            />
          </FormControl>
          <FormControl label='Charge Type'>
            <SelectFilter
              options={chargeTypeOptions}
              value={mapSelectOption(chargeTypeOptions, settings.serviceChargeType) ?? chargeTypeOptions[0]}
              onChange={handleChargeTypeChange}
            />
          </FormControl>
          <FormControl label='Charge Amount *'>
            <FormattedInput
              format={isServiceChargeTypeAmount ? 'money' : 'percentage'}
              value={serviceChargeAmountValue(settings) ?? ''}
              onChange={handleChargeAmountChange}
              placeholder={isServiceChargeTypeAmount ? '0.00' : '0'}
            />
          </FormControl>
        </>
      )}

      {posIntegration === PosSystemType.bypass2 && (
        <>
          <FormControl label='Service Charge Method'>
            <SelectFilter
              options={serviceChargeMethodOptions}
              value={mapSelectOption(serviceChargeMethodOptions, serviceChargeMethod) ?? serviceChargeMethodOptions[0]}
              onChange={handleServiceChargeMethodChange}
            />
          </FormControl>
          
          {isMenuItemBased ? (
            <FormControl label='Select POS Item *'>
              <PosItemSelect
                companyId={companyId}
                locationId={id}
                onChange={(option: Option) => {
                  updateLocationParam(option?.value ?? '', LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_POS_ITEM);
                }}
                selectedPosItemId={settings.serviceChargeId}
              ></PosItemSelect>
            </FormControl>
          ) : (
            <>
              <FormControl label='Charge Type'>
                <SelectFilter
                  options={chargeTypeOptions}
                  value={mapSelectOption(chargeTypeOptions, settings.serviceChargeType) ?? chargeTypeOptions[0]}
                  onChange={handleChargeTypeChange}
                />
              </FormControl>
              <FormControl label='Charge Amount *'>
                <FormattedInput
                  format={isServiceChargeTypeAmount ? 'money' : 'percentage'}
                  value={serviceChargeAmountValue(settings) ?? ''}
                  onChange={handleChargeAmountChange}
                  placeholder={isServiceChargeTypeAmount ? '0.00' : '0'}
                />
              </FormControl>
              <FormControl label='Service Charge Pre-Tax'>
                <Toggle
                  checked={!!settings.serviceChargePreTax}
                  onChange={(checked) => {
                    updateLocationParam(checked, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_PRE_TAX);
                  }}
                />
              </FormControl>
              <FormControl label='Service Charge Tax Rate'>
                <FormattedInput
                  format='percentage'
                  value={settings.serviceChargeTaxRate ?? ''}
                  onChange={(event) => {
                    updateLocationParam(parseFloat(event.target.value) || 0, LOCATION_SETTINGS_ACTIONS.UPDATE_FORCED_SERVICE_CHARGE_TAX_RATE);
                  }}
                  placeholder='0'
                />
              </FormControl>
            </>
          )}
        </>
      )}
    </>
  );
};

export default ForcedServiceCharges;
